import { type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import type { SxProps } from '@mui/material/styles';
import PersonIcon from '@mui/icons-material/Person';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import { useAppStore } from '../store/useAppStore';

/**
 * Signature "coach" surface: a soft navy→paper→warm-accent gradient used for
 * the one hero moment per screen. Everything else stays a plain card, so the
 * hero reads as the place to look first instead of one more white rectangle.
 */
export const heroSurfaceSx: SxProps<Theme> = {
  p: { xs: 2.25, sm: 3 },
  overflow: 'hidden',
  border: '1px solid',
  borderColor: (t: Theme) => alpha(t.palette.primary.main, 0.18),
  borderRadius: 3,
  background: (t: Theme) =>
    `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.09)}, ${t.palette.background.paper} 55%, ${alpha(
      t.palette.accent.main,
      0.1,
    )})`,
  boxShadow: (t: Theme) =>
    t.palette.mode === 'dark' ? '0 18px 42px rgba(0,0,0,0.24)' : '0 18px 42px rgba(31,58,95,0.08)',
};

/**
 * A short coach-voice cue list (the "advice in the margin"). Bullets use the
 * warm accent so guidance moments read differently from data.
 */
export function CoachCues({ title = "Coach's cues", cues }: { title?: string; cues: string[] }) {
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, color: 'accent.dark' }}>
        {title}
      </Typography>
      <Stack spacing={1}>
        {cues.map((cue) => (
          <Stack key={cue} direction="row" spacing={1} alignItems="flex-start">
            <Box
              sx={{
                width: 7,
                height: 7,
                mt: 0.85,
                borderRadius: '50%',
                bgcolor: 'accent.main',
                flexShrink: 0,
              }}
            />
            <Typography variant="body2" color="text.secondary">
              {cue}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
}) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'stretch', sm: 'flex-start' }}
      justifyContent="space-between"
      sx={{ mb: { xs: 2.5, md: 3 } }}
      flexWrap="wrap"
      gap={2}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.4rem', sm: '1.75rem' } }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 760 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {action && (
        <Box
          sx={{
            flexShrink: 0,
            // Action buttons wrap under the title on phones instead of squeezing it.
            '& .MuiButton-root': { whiteSpace: 'nowrap' },
          }}
        >
          {action}
        </Box>
      )}
    </Stack>
  );
}

export function EmptyState({
  title,
  hint,
  icon,
  actions,
}: {
  title: string;
  hint?: string;
  icon?: ReactNode;
  /** Contextual CTAs (buttons/links) so no screen is ever a dead end. */
  actions?: { label: string; to: string; variant?: 'contained' | 'outlined' }[];
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 3, sm: 5 },
        textAlign: 'center',
        color: 'text.secondary',
        borderStyle: 'dashed',
        borderColor: (t) => alpha(t.palette.accent.main, 0.35),
        bgcolor: (t) => alpha(t.palette.accent.main, 0.04),
        borderRadius: 3,
      }}
    >
      <Box sx={{ mb: 1, '& svg': { fontSize: 40, color: 'accent.main' } }}>
        {icon ?? <SelfImprovementIcon />}
      </Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
        {title}
      </Typography>
      {hint && (
        <Typography variant="body2" sx={{ mt: 1, maxWidth: 420, mx: 'auto' }}>
          {hint}
        </Typography>
      )}
      {actions && actions.length > 0 && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          justifyContent="center"
          sx={{ mt: 2.5 }}
        >
          {actions.map((a, i) => (
            <Button
              key={a.to + a.label}
              component={RouterLink}
              to={a.to}
              variant={a.variant ?? (i === 0 ? 'contained' : 'outlined')}
            >
              {a.label}
            </Button>
          ))}
        </Stack>
      )}
    </Paper>
  );
}

/**
 * Inline nudge shown before profile-driven AI features when the structured
 * profile hasn't been generated yet: the output quality depends on it, so we
 * send the student to Student Profile first instead of producing generic text.
 * Renders nothing once the profile exists.
 */
export function ProfileNudge({ feature }: { feature: string }) {
  const profile = useAppStore((s) => s.profile);
  if (profile?.structured) return null;
  return (
    <Alert
      severity="info"
      icon={<PersonIcon />}
      sx={{ mb: 2, alignItems: 'center' }}
      action={
        <Button component={RouterLink} to="/profile" variant="contained" size="small" sx={{ whiteSpace: 'nowrap' }}>
          Set up my profile
        </Button>
      }
    >
      {feature} works best with your interview profile. Add your materials once and every answer,
      grade, and plan is personalized to you.
    </Alert>
  );
}

export function Loading({ label = 'Working…' }: { label?: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ color: 'text.secondary' }}>
      <CircularProgress size={18} />
      <Typography variant="body2">{label}</Typography>
    </Stack>
  );
}

export function scoreColor(score: number): 'success' | 'warning' | 'error' {
  if (score >= 75) return 'success';
  if (score >= 50) return 'warning';
  return 'error';
}

export function ScoreBadge({ score, size = 56 }: { score: number; size?: number }) {
  const color = scoreColor(score);
  const palette = { success: '#2e7d57', warning: '#b26a00', error: '#b3402f' }[color];
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        border: `4px solid ${palette}`,
        color: palette,
        display: 'grid',
        placeItems: 'center',
        fontWeight: 800,
        fontSize: size * 0.32,
        flexShrink: 0,
      }}
    >
      {Math.round(score)}
    </Box>
  );
}

export function ConfidenceChip({ confidence }: { confidence: 'high' | 'medium' | 'low' }) {
  const map = {
    high: { color: 'success' as const, label: 'High confidence' },
    medium: { color: 'warning' as const, label: 'Medium confidence' },
    low: { color: 'default' as const, label: 'Low confidence' },
  };
  const m = map[confidence];
  return <Chip size="small" color={m.color} variant="outlined" label={m.label} />;
}
